<script setup lang="ts" name="AdminPortCallsIndex">
import { reactive, ref } from 'vue';

import { useLastUpdated } from '~/features/useLastSync';
import { mapVoyageToTimelineRow } from '~/features/usePortCallsTimeline';
import { useService } from '~/features/useService';
import { getIdsFromArray } from '~/features/useUrlSearch';

import Service from '~/services/Service.js';

import AdminPortCallsFilters from '~/components/AdminPortCallsFilters.vue';
import BasePage from '~/components/BasePage.vue';
import ServiceCard from '~/components/ServiceCard.vue';
import TimelineChart from '~/components/TimelineChart.vue';

import type {
  IAdminPortCallsFilters,
  IAdminPortCallsQuery,
  IPageResponse,
  IPeriod,
  IPortCallVoyage,
  ITimelineChartBaseRow,
} from '~/types';

const { lastUpdated, setLastUpdated } = useLastUpdated();

const voyageRows = ref<ITimelineChartBaseRow[]>([]);

const filtersQuery = reactive<IAdminPortCallsQuery>({
  ports: [],
  period: {} as IPeriod,
  tradelanes: [],
  agencies: [],
});

const filterOptions = ref<IAdminPortCallsFilters>({
  ports: [],
  tradelanes: [],
  agencies: [],
});

const onSearchClear = () => {
  filtersQuery.ports = [];
  filtersQuery.period = {} as IPeriod;
  filtersQuery.tradelanes = [];
  filtersQuery.agencies = [];
};

Service.allPortCalls()
  .filters()
  .onSuccess(({ data }: { data: IAdminPortCallsFilters }) => {
    filterOptions.value.ports = data.ports;
    filterOptions.value.tradelanes = data.tradelanes;
    filterOptions.value.agencies = data.agencies;
  })
  .execute();

const serviceState = useService(
  Service.allPortCalls().index(),
  ({ data, meta }: IPageResponse<IPortCallVoyage[]>) => {
    if (!data.length) return false;
    voyageRows.value = mapVoyageToTimelineRow(data);
    setLastUpdated(meta?.lastUpdated);
    return true;
  },
  {
    query: filtersQuery,
    getQuery: () => ({
      ports: getIdsFromArray(filtersQuery.ports),
      tradelanes: getIdsFromArray(filtersQuery.tradelanes),
      agencies: getIdsFromArray(filtersQuery.agencies),
      cp_start: filtersQuery.period.cp_start,
      cp_end: filtersQuery.period.cp_end,
    }),
  },
);
</script>

<template>
  <BasePage title="All Port Calls" :last-updated="lastUpdated">
    <div class="col-span-full space-y-2">
      <AdminPortCallsFilters
        v-if="!serviceState.stateHasErrors"
        v-model="filtersQuery"
        :options="filterOptions"
      />
      <ServiceCard v-bind="serviceState" styled @clear="onSearchClear">
        <TimelineChart :rows="voyageRows" />
      </ServiceCard>
    </div>
  </BasePage>
</template>
